import React, { useEffect, useState, useCallback } from "react";
import { Search, ScrollText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, LoadingBlock, EmptyState, formatDateTime } from "@/components/Common";
import { useL } from "@/context/I18nContext";
import api from "@/lib/api";

export default function AdminAudit() {
  const L = useL();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/audit-logs", { params: { q: q || undefined, page, page_size: 30 } });
    setItems(data.items); setTotal(data.total);
  }, [q, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl font-medium text-white">{L("Log di audit", "Audit log", "Audit-Protokoll")}</h2>
      <p className="text-sm text-slate-500">{L("I log di audit sono immutabili e non modificabili.", "Audit logs are immutable and cannot be modified.", "Audit-Protokolle sind unveränderlich und können nicht bearbeitet werden.")}</p>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder={L("Cerca azione, attore, entità…", "Search action, actor, entity…", "Aktion, Akteur, Entität suchen…")} className="pl-9 bg-navy-paper border-white/10 max-w-md" data-testid="audit-search" />
      </div>
      {items === null ? <LoadingBlock /> : items.length === 0 ? <EmptyState icon={ScrollText} title={L("Nessun evento", "No events", "Keine Ereignisse")} /> : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-4 py-3">{L("Azione", "Action", "Aktion")}</th><th className="text-left px-4 py-3">{L("Entità", "Entity", "Entität")}</th><th className="text-left px-4 py-3">{L("Attore", "Actor", "Akteur")}</th><th className="text-left px-4 py-3">IP</th><th className="text-left px-4 py-3">{L("Data", "Date", "Datum")}</th></tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`audit-${a.id}`}>
                  <td className="px-4 py-2.5 text-cyanx">{a.action}</td>
                  <td className="px-4 py-2.5 text-slate-300">{a.entity_type}</td>
                  <td className="px-4 py-2.5 text-slate-400">{a.actor_email || "—"} <span className="text-slate-600">({a.role || "—"})</span></td>
                  <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{a.ip_address || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500">{formatDateTime(a.timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {total > 30 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">{total} {L("eventi", "events", "Ereignisse")}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-white/20" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>{L("Precedente", "Previous", "Zurück")}</Button>
            <Button size="sm" variant="outline" className="border-white/20" disabled={page * 30 >= total} onClick={() => setPage((p) => p + 1)}>{L("Successiva", "Next", "Weiter")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
